import { useCallback, useEffect, useRef, useState } from 'react'
import { Drawer } from '@mantine/core'
import type { ReactNode } from 'react'

const MIN_W = 360
const maxW = () => Math.round(window.innerWidth * 0.9)

export function ResizableDrawer({
  opened, onClose, title, storageKey, defaultWidth = 640, children,
}: {
  opened: boolean
  onClose: () => void
  title?: ReactNode
  storageKey: string
  defaultWidth?: number
  children: ReactNode
}) {
  const [width, setWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(storageKey))
    return saved >= MIN_W ? saved : defaultWidth
  })
  const dragging = useRef(false)

  const onMove = useCallback((e: MouseEvent) => {
    if (!dragging.current) return
    const w = Math.min(maxW(), Math.max(MIN_W, window.innerWidth - e.clientX))
    setWidth(w)
  }, [])
  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    document.body.style.userSelect = ''
    localStorage.setItem(storageKey, String(width))
  }, [storageKey, width])

  useEffect(() => {
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [onMove, onUp])

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size={width} title={title}>
      <div
        data-testid="drawer-resize-handle"
        onMouseDown={() => { dragging.current = true; document.body.style.userSelect = 'none' }}
        style={{ position: 'absolute', top: 0, left: 0, width: 6, height: '100%', cursor: 'col-resize', zIndex: 1000 }}
      />
      {children}
    </Drawer>
  )
}
